import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { Button } from "@/components/_atoms/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/_atoms/form";
import { NumberInput } from "@/components/_atoms/number-input";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCreateMunicipalPropertyRegister } from "@/domain/municipal-property/hooks/api/use-create-municipal-property-register";
import { isActiveRegisterConflict } from "@/domain/municipal-property/lib/is-active-register-conflict";
import { buildMunicipalPropertyRegisterCreateFormSchema } from "@/domain/municipal-property/models/build-municipal-property-register-create-form-schema";
import { MunicipalPropertyRegisterCreateFormValues } from "@/domain/municipal-property/models/municipal-property-register-create-form-values";
import { MUNICIPAL_PROPERTY_REGISTER_YEAR_BOUNDS } from "@ark/shared";
import { zodResolver } from "@hookform/resolvers/zod";

type RegisterCreateSubmitValues = z.output<ReturnType<typeof buildMunicipalPropertyRegisterCreateFormSchema>>;

interface MunicipalPropertyRegisterCreateFormProps {
  workspaceId: string;
  defaultYear: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export function MunicipalPropertyRegisterCreateForm({
  workspaceId,
  defaultYear,
  onSuccess,
  onCancel,
}: MunicipalPropertyRegisterCreateFormProps) {
  const { t } = useDomainTranslation("municipal-property");
  const createRegister = useCreateMunicipalPropertyRegister();

  const schema = useMemo(
    () =>
      buildMunicipalPropertyRegisterCreateFormSchema({
        messages: {
          yearRange: t("register.create.validation.yearRange", MUNICIPAL_PROPERTY_REGISTER_YEAR_BOUNDS),
        },
      }),
    [t],
  );

  const form = useForm<MunicipalPropertyRegisterCreateFormValues, unknown, RegisterCreateSubmitValues>({
    resolver: zodResolver(schema),
    defaultValues: { year: defaultYear },
  });

  async function handleSubmit(values: RegisterCreateSubmitValues) {
    try {
      await createRegister.mutateAsync({ workspaceId, year: values.year });
      toast.success(t("register.create.success"));
      onSuccess();
    } catch (error) {
      toast.error(isActiveRegisterConflict(error) ? t("register.create.conflict") : t("register.create.error"));
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="year"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("register.create.field.year")}</FormLabel>
              <FormControl>
                <NumberInput value={field.value} onValueChange={field.onChange} inputMode="numeric" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" size="xs" onClick={onCancel}>
            {t("register.create.cancel")}
          </Button>
          <Button type="submit" size="xs" disabled={createRegister.isPending}>
            {createRegister.isPending ? t("register.create.submitting") : t("register.create.submit")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
